/**
 * Photo Reel — upload 1-15 pics, generate a hybrid Kling+slideshow video.
 *
 * Token cost: 1 pic = 10 tokens, 2+ pics = 20 tokens flat (only first + last
 * get the Kling treatment; middle frames are FFmpeg Ken Burns).
 */

import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AssistantChat } from "../components/AssistantChat";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Image as ImageIcon, Upload, X, GripVertical, Play, Loader2, Coins, Sparkles,
  MessageSquareText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 15;

export default function PhotoReelPage() {
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);       // [{ file, url, id }]
  const [prompt, setPrompt] = useState("Cinematic product shot, smooth motion");
  const [aspect, setAspect] = useState("9:16");
  const [submitting, setSubmitting] = useState(false);
  const dragIdx = useRef(null);
  const fileInput = useRef(null);

  const isUltimate = user?.subscription_tier === "ultimate";
  const cost = images.length === 0 ? 0 : (images.length === 1 ? 10 : 20);

  const addFiles = (files) => {
    const incoming = Array.from(files).slice(0, MAX_IMAGES - images.length);
    const enriched = incoming.map((f) => ({
      file: f,
      url:  URL.createObjectURL(f),
      id:   `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
    }));
    setImages((prev) => [...prev, ...enriched].slice(0, MAX_IMAGES));
  };

  const removeAt = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const onDragStart = (i) => () => { dragIdx.current = i; };
  const onDragOver  = (e) => { e.preventDefault(); };
  const onDrop      = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setImages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIdx.current = null;
  };

  const handleSubmit = async () => {
    if (images.length === 0) {
      toast.error("Pick at least 1 image");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      fd.append("aspect_ratio", aspect);
      fd.append("model", "kling-v3");
      images.forEach((img) => fd.append("images", img.file, img.file.name));
      const r = await axios.post(`${API}/api/video-gen/photo-reel`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(`Reel queued · ${r.data.cost === 0 ? "free" : `−${r.data.cost} tokens`} — share it to Community once ready!`);
      refreshUser?.();
      navigate("/dashboard/tasks");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start reel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="photo-reel-page"
    >
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">New</p>
        <h1 className="text-3xl font-bold text-ink-950 mt-1">Photo Reel</h1>
        <p className="text-ink-500 mt-2 max-w-2xl">
          Drop 1–15 photos. We animate the first and last with Kling AI and stitch the rest as a
          Ken-Burns slideshow with smooth crossfades. Flat cost — <strong>10 tokens</strong> for a single
          pic, <strong>20 tokens</strong> for any count from 2-15.
        </p>
      </header>

      {/* Inline XEA Assistant — chat to craft a great Kling prompt, or
          attach a reference picture so the animated frames match a specific
          mood/style. Click "Use as prompt" on any AI reply to auto-fill
          the prompt box below. Free to chat; tokens only on Generate. */}
      <div
        className="relative rounded-3xl bg-gradient-to-br from-ink-50 via-white to-coral-500/5 border border-ink-200/70 p-1"
        data-testid="reel-assistant-wrap"
      >
        <div className="rounded-[1.35rem] bg-white">
          <div className="px-5 pt-4 pb-2 flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ink-950 shrink-0">
              <Sparkles className="w-4 h-4 text-citrus-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral-500">
                XEA Assistant · free to chat
              </p>
              <p className="text-sm font-display font-bold text-ink-950">
                Tell me the vibe — or attach a picture I should match
              </p>
              <p className="text-xs text-ink-500 mt-0.5">
                Try: &ldquo;Slow cinematic push-in, golden hour, soft bokeh&rdquo; or upload a moodboard
                with 📎 — I&apos;ll write a Kling-ready prompt that nails it. Click{" "}
                <span className="font-semibold text-ink-700">Use as prompt</span> on any reply.
              </p>
            </div>
          </div>
          <div className="px-2 pb-2">
            <AssistantChat
              variant="embed"
              contextHint="Describe the motion + mood, or attach a reference image…"
              usePromptLabel="Use as prompt"
              enableImageUpload={true}
              onUsePrompt={(text) => {
                setPrompt(text.slice(0, 400));
                toast.success("Prompt locked in for the animated frames");
              }}
            />
          </div>
        </div>
      </div>

      {/* Uploader */}
      <div
        className="rounded-2xl border-2 border-dashed border-ink-300 bg-ink-50 p-8 text-center cursor-pointer hover:border-coral-500 transition-colors"
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        data-testid="photo-reel-dropzone"
      >
        <input
          ref={fileInput} type="file" accept="image/*" multiple hidden
          onChange={(e) => addFiles(e.target.files)}
          data-testid="photo-reel-file-input"
        />
        <Upload className="w-8 h-8 text-ink-500 mx-auto mb-2" />
        <p className="text-sm font-bold text-ink-950">
          Drop images or click to upload · {images.length}/{MAX_IMAGES}
        </p>
        <p className="text-xs text-ink-500 mt-1">JPG, PNG, WebP, HEIC — max 10 MB each</p>
      </div>

      {/* Thumbnails grid with drag reorder */}
      <AnimatePresence>
        {images.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="grid grid-cols-3 md:grid-cols-5 gap-3"
            data-testid="photo-reel-grid"
          >
            {images.map((img, idx) => (
              <div
                key={img.id}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={onDrop(idx)}
                className="relative aspect-square rounded-xl overflow-hidden bg-ink-100 group cursor-move"
                data-testid={`photo-reel-thumb-${idx}`}
              >
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <GripVertical className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                </div>
                <span className="absolute top-1.5 left-1.5 bg-ink-950 text-white text-[10px] font-bold uppercase px-1.5 py-0.5 rounded">
                  {idx + 1}
                  {(idx === 0 || idx === images.length - 1) && images.length > 1 && (
                    <span className="ml-1 text-citrus-500">★</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeAt(idx); }}
                  className="absolute top-1.5 right-1.5 bg-ink-950/80 hover:bg-rose-500 text-white rounded-full p-1"
                  data-testid={`photo-reel-remove-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {images.length > 1 && (
        <p className="text-[11px] text-ink-500 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-citrus-500" />
          <strong className="text-ink-950">★</strong> = Kling-animated (first + last frame). Drag thumbnails to reorder.
        </p>
      )}

      {/* Prompt + aspect */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-ink-950 flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-coral-500" />
            Prompt for the animated frames
          </Label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            maxLength={400}
            className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm text-ink-950 focus:outline-none focus:border-ink-950"
            placeholder="e.g., Cinematic product shot, slow camera push-in, soft lighting"
            data-testid="photo-reel-prompt"
          />
          <p className="text-[11px] text-ink-500">
            Tip: use the chat above to draft a richer prompt — works especially well if you attach a reference picture.
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-ink-950">Aspect ratio</Label>
          <div className="grid grid-cols-3 gap-2" data-testid="photo-reel-aspect">
            {[
              { v: "9:16", label: "Portrait" },
              { v: "1:1",  label: "Square" },
              { v: "16:9", label: "Wide" },
            ].map((o) => {
              const active = aspect === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setAspect(o.v)}
                  data-testid={`photo-reel-aspect-${o.v}`}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    active ? "bg-ink-950 text-white border-ink-950" : "bg-white border-ink-200 hover:border-ink-400"
                  }`}
                >
                  <p className="text-sm font-bold">{o.v}</p>
                  <p className={`text-[10px] ${active ? "text-white/70" : "text-ink-500"}`}>{o.label}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-ink-200">
        <div className="text-xs text-ink-500 flex items-center gap-2">
          <Coins className="w-3.5 h-3.5 text-citrus-500" />
          <span>
            <strong className="text-ink-950">{isUltimate ? "Free (Ultimate)" : `${cost} tokens`}</strong>
            {images.length > 0 && ` · ~${images.length >= 3 ? "2-4" : "1-2"} min render`}
          </span>
        </div>
        <Button
          size="lg"
          onClick={handleSubmit}
          disabled={submitting || images.length === 0}
          className="bg-coral-500 hover:bg-coral-600 text-white"
          data-testid="photo-reel-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {submitting ? "Starting…" : "Generate reel"}
        </Button>
      </div>
    </motion.div>
  );
}
